import { FC, useState } from "react";
import { CalculatorImage } from "../CalculatorImage";
import { CalculatorButton } from "../components/CalculatorButton";
import { MenuItem } from "@material-ui/core";
import {
  CalculatorBodyText,
  CalculatorHeadline,
  PreCalculatorHeadline,
  CalculatorTopicH2Headline,
  ListLinkText,
  CalculatorTopicHeadline,
} from "../CalculatorTypography";
import { makeStyles, useMediaQuery } from "@material-ui/core";
import useTranslation from "next-translate/useTranslation";
import { TheCycleverseTheme } from "src/theme";
import CycleverseDivider from "src/components/CycleverseDivider";
import { CalculatorResultsTitle } from "../components/CalculatorResultsTitle";
import { InputComponent, SelectorComponent } from "../CalculatorSelectInput";
import { InputHeader } from "../KalorienverbrauchRechner/InputHeader";
import InputWithMarginBottom from "../InputWithMarginBottom";
import ResultWithColors from "../ResultWithColors";

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  link: {
    textDecoration: "underline",
    color: theme.openButtonColor,
  },
  overline1: {
    marginTop: "12px",
  },
  flex: {
    display: "flex",
  },
  timeInput: {
    display: "flex",
    marginLeft: "40px",
    "@media (max-width: 578px)": {
      flexDirection: "column",
    },
  },
  marginLeft: {
    marginLeft: "30px",
  },
  marginRight: {
    marginRight: "30px",
  },
  InBetween: {
    marginTop: "10px",
    marginLeft: "8px",
    marginRight: "8px",
    "@media (min-width: 578px)": {
      marginLeft: "30px",
      marginRight: "30px",
    },
  },
  marginBottom: {
    marginBottom: "32px",
  },
  imgDescription: {
    fontSize: "16px !important",
    lineHeight: "23px",
  },

  alignCenter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  errorMessage: {
    fontSize: "16px",
    color: theme.productPageTeaserHeadlineColor,
    textAlign: "center",
    width: "75%",
    margin: "auto",
    marginTop: "10px",
    "@media (max-width: 488px)": {
      width: "100%",
    },
  },
  divider: {
    width: "100%",
    height: "1px",
    backgroundColor: theme.dividerBgColor,
  },
  dividerLabel: {
    width: "40px",
    height: "40px",
    borderRadius: "20px",
    marginTop: "-20.5px",
  },
  dividerContainer: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    alignItems: "center",
    alignContent: "center",
    justifyItems: "center",
    justifyContent: "center",
    marginTop: "50px",
  },
  listLinkHeader: {
    color: theme.grayColor,
    fontSize: "24px",
    marginTop: "15px",
    fontFamily: "inherit",
    fontWeight: 300,
    lineHeight: 1.1,
    marginBottom: "5px",
  },
  listLinkContainer: {
    display: "flex",
    flexDirection: "column",
    margin: "20px 0",
  },

  headingTopMargin: {
    marginTop: "50px",
  },
}));

type Time = { Z1: number | string; Z2: number | string; Z3: number | string };

type Values = {
  field0a: string;
  field0b: string;
  field1b: string;
  field3bc: string;
  GeschwindigkeitValue: number;
  StreckeValue: number;
  stunden: number;
  minuten: number;
  sekunden: number;
  hasError: boolean;
  speed: number | string;
  displayUnit: string;
  zeitValue: number;
  time: Time;
  distance: number | string;
};

const GeschwindigkeitRechner: FC = () => {
  const isSmallDevice = useMediaQuery("(max-width: 585px)");
  const classes = useStyles();
  const { t, lang } = useTranslation("geschwindigkeitRechner");

  const initialState = {
    field0a: t("field0aOption1"),
    field0b: t("field0bOption1"),
    field1b: t("field1bOption1"),
    field3bc: t("field0bOption1"),
    GeschwindigkeitValue: 0,
    StreckeValue: null,
    stunden: 0,
    minuten: 0,
    sekunden: 0,
    hasError: false,
    speed: null,
    displayUnit: "",
    zeitValue: 0,
    time: { Z1: null, Z2: null, Z3: null },
    distance: null,
  };

  const [values, setValues] = useState<Values>(initialState);

  const handleInputOnChange = (name, value) => {
    const onlyNumbersRegex = /^[0-9]*([,][0-9]*)?$/;

    setValues({ ...values, distance: null, speed: null, time: { Z1: null, Z2: null, Z3: null } });

    if (name === "field0b") {
      if (value?.target?.value === t("field0bOption1")) {
        setValues({ ...values, ["field1b"]: t("field1bOption1") });
      } else if (value?.target?.value === t("field0bOption2")) {
        setValues({
          ...values,
          ["field1b"]: t("field1bOption2"),
          ["field3bc"]: t("field0bOption2"),
        });
      } else if (value?.target?.value === t("field0bOption3")) {
        setValues({
          ...values,
          ["field1b"]: t("field1bOption3"),
          ["field3bc"]: t("field0bOption3"),
        });
      } else if (value?.target?.value === t("field1bOption1")) {
        setValues({ ...values, ["field3bc"]: t("field0bOption1") });
      } else if (value?.target?.value === t("field1bOption2")) {
        setValues({ ...values, ["field3bc"]: t("field0bOption2") });
      } else if (value?.target?.value === t("field1bOption3")) {
        setValues({ ...values, ["field3bc"]: t("field0bOption3") });
      }
    }

    if (name === "field0a") {
      setValues({ ...values, [name]: value?.target?.value });
    } else if (
      name === "GeschwindigkeitValue" ||
      name === "StreckeValue" ||
      name === "stunden" ||
      name === "minuten" ||
      name === "sekunden" ||
      name === "zeitValue"
    ) {
      setValues((el) => ({
        ...el,
        [name]: onlyNumbersRegex.test(value?.target?.value) ? +value?.target?.value : 0,
      }));
    } else {
      setValues((el) => ({
        ...el,
        [name]: value?.target?.value,
      }));
    }

    if (value.target.value === t("field0aOption3")) {
      setValues({ ...values, [name]: value?.target?.value, ["field0b"]: t("field1bOption1") });
    } else if (value.target.value === t("field0aOption1")) {
      setValues({ ...values, [name]: value?.target?.value, ["field0b"]: t("field0bOption1") });
    }
  };

  const twoDecimalPoints = (value: number): string => {
    return value.toLocaleString(lang, { maximumFractionDigits: 2 });
  };

  const minutesValue = values?.minuten ? values?.minuten / 60 : 0;
  const secondsValue = values?.sekunden ? values?.sekunden / 3600 : 0;

  const calculateSpeed = (): number | string => {
    if (
      (!values?.sekunden && !values?.minuten && !values?.stunden) ||
      !values.field0a ||
      !values?.field0b ||
      !values?.field1b ||
      !values?.StreckeValue
    ) {
      setValues((el) => ({
        ...el,
        hasError: true,
      }));
    } else {
      if (values?.field0a === t("field0aOption1")) {
        if (values?.field0b === t("field0bOption1") && values?.field1b === t("field1bOption1")) {
          const result = values?.StreckeValue / (values?.stunden + minutesValue + secondsValue);
          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field0bOption1") && values?.field1b === t("field1bOption2")) {
          const result = values?.StreckeValue / (values?.stunden + minutesValue + secondsValue) / 1000;
          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field0bOption1") && values?.field1b === t("field1bOption3")) {
          const result = (values?.StreckeValue / (values?.stunden + minutesValue + secondsValue)) * 1.60934;
          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field0bOption2") && values?.field1b === t("field1bOption1")) {
          const result = values?.StreckeValue / (values?.stunden + minutesValue + secondsValue) / 3.6;
          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field0bOption2") && values?.field1b === t("field1bOption2")) {
          const result = values?.StreckeValue / (values?.stunden + minutesValue + secondsValue) / 3600;
          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field0bOption2") && values?.field1b === t("field1bOption3")) {
          const result = (values?.StreckeValue / (values?.stunden + minutesValue + secondsValue)) * 2.237;
          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field0bOption3") && values?.field1b === t("field1bOption1")) {
          const result = values?.StreckeValue / (values?.stunden + minutesValue + secondsValue) / 1.60934;
          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field0bOption3") && values?.field1b === t("field1bOption2")) {
          const result = values?.StreckeValue / (values?.stunden + minutesValue + secondsValue) / 1609.34;
          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field0bOption3") && values?.field1b === t("field1bOption3")) {
          const result = values?.StreckeValue / (values?.stunden + minutesValue + secondsValue);
          return twoDecimalPoints(result);
        }
      }
    }
  };

  const calculateTime = (): { Z1: number; Z2: number; Z3: number | string } => {
    if (!values?.GeschwindigkeitValue && !values?.StreckeValue) {
      setValues((el) => ({
        ...el,
        hasError: true,
      }));
    } else {
      if (values?.field0a === t("field0aOption2")) {
        if (values?.field1b === t("field1bOption1") && values?.field3bc === t("field0bOption1")) {
          let z1 = Math.floor(values?.StreckeValue / values?.GeschwindigkeitValue);
          let z2 = Math.floor((values?.StreckeValue / values?.GeschwindigkeitValue - z1) * 60);
          let z3 = Math.round((values?.StreckeValue / values?.GeschwindigkeitValue - z1 - z2 / 60) * 3600);

          return { Z1: z1, Z2: z2, Z3: z3 };
        } else if (values?.field1b === t("field1bOption1") && values?.field3bc === t("field0bOption2")) {
          let z1 = Math.floor((3.6 * values?.StreckeValue) / values?.GeschwindigkeitValue);
          let z2 = Math.floor(
            parseFloat((((3.6 * values?.StreckeValue) / values?.GeschwindigkeitValue - z1) * 60).toPrecision(12))
          );
          const firstPart = parseFloat(
            ((3.6 * values?.StreckeValue) / values?.GeschwindigkeitValue - z1).toPrecision(12)
          );
          const secondPart = parseFloat((z2 / 60).toPrecision(12));
          let z3 = parseFloat(((firstPart - secondPart) * 3600).toPrecision(12));

          return { Z1: z1, Z2: z2, Z3: z3 };
        } else if (values?.field1b === t("field1bOption1") && values?.field3bc === t("field0bOption3")) {
          let z1 = Math.floor(values?.StreckeValue / values?.GeschwindigkeitValue / 1.60934);
          let z2 = Math.floor((values?.StreckeValue / values?.GeschwindigkeitValue / 1.60934 - z1) * 60);
          let z3 = Math.round((values?.StreckeValue / values?.GeschwindigkeitValue / 1.60934 - z1 - z2 / 60) * 3600);

          return { Z1: z1, Z2: z2, Z3: z3 };
        } else if (values?.field1b === t("field1bOption2") && values?.field3bc === t("field0bOption1")) {
          let z1 = Math.floor(values?.StreckeValue / values?.GeschwindigkeitValue / 1000);
          let z2 = Math.floor((values?.StreckeValue / values?.GeschwindigkeitValue / 1000 - z1) * 60);
          let z3 = twoDecimalPoints((values?.StreckeValue / values?.GeschwindigkeitValue / 1000 - z1 - z2 / 60) * 3600);

          return { Z1: z1, Z2: z2, Z3: z3 };
        } else if (values?.field1b === t("field1bOption2") && values?.field3bc === t("field0bOption2")) {
          let z1 = Math.floor(values?.StreckeValue / values?.GeschwindigkeitValue / 3600);
          let z2 = Math.floor((values?.StreckeValue / values?.GeschwindigkeitValue - z1 * 3600) / 60);
          let z3 = twoDecimalPoints(values?.StreckeValue / values?.GeschwindigkeitValue - z1 * 3600 - z2 * 60);

          return { Z1: z1, Z2: z2, Z3: z3 };
        } else if (values?.field1b === t("field1bOption2") && values?.field3bc === t("field0bOption3")) {
          let z1 = Math.floor(values?.StreckeValue / values?.GeschwindigkeitValue / 1609.34);
          let z2 = Math.floor((values?.StreckeValue / values?.GeschwindigkeitValue / 1609.34 - z1) * 60);
          let z3 = twoDecimalPoints(
            (values?.StreckeValue / values?.GeschwindigkeitValue / 1609.34 - z1 - z2 / 60) * 3600
          );

          return { Z1: z1, Z2: z2, Z3: z3 };
        } else if (values?.field1b === t("field1bOption3") && values?.field3bc === t("field0bOption1")) {
          let z1 = Math.floor((1.60934 * values?.StreckeValue) / values?.GeschwindigkeitValue);
          let z2 = Math.floor(((1.60934 * values?.StreckeValue) / values?.GeschwindigkeitValue - z1) * 60);
          let z3 = Math.round(((1.60934 * values?.StreckeValue) / values?.GeschwindigkeitValue - z1 - z2 / 60) * 3600);

          return { Z1: z1, Z2: z2, Z3: z3 };
        } else if (values?.field1b === t("field1bOption3") && values?.field3bc === t("field0bOption2")) {
          let z1 = Math.floor((5.79362 * values?.StreckeValue) / values?.GeschwindigkeitValue);
          let z2 = Math.floor(((5.79362 * values?.StreckeValue) / values?.GeschwindigkeitValue - z1) * 60);
          let z3 = Math.round(((5.79362 * values?.StreckeValue) / values?.GeschwindigkeitValue - z1 - z2 / 60) * 3600);

          return { Z1: z1, Z2: z2, Z3: z3 };
        } else if (values?.field1b === t("field1bOption3") && values?.field3bc === t("field0bOption3")) {
          let z1 = Math.floor(values?.StreckeValue / values?.GeschwindigkeitValue);
          let z2 = Math.floor((values?.StreckeValue / values?.GeschwindigkeitValue - z1) * 60);
          let z3 = Math.round((values?.StreckeValue / values?.GeschwindigkeitValue - z1 - z2 / 60) * 3600);

          return { Z1: z1, Z2: z2, Z3: z3 };
        }
      }
    }
  };

  const calculateDistance = (): any => {
    if (
      (!values?.sekunden && !values?.minuten && !values?.stunden) ||
      !values.field0a ||
      !values?.field0b ||
      !values?.field3bc ||
      !values?.GeschwindigkeitValue
    ) {
      setValues((el) => ({
        ...el,
        hasError: true,
      }));
    } else {
      if (values?.field0a === t("field0aOption3")) {
        if (values?.field0b === t("field1bOption1") && values?.field3bc === t("field0bOption1")) {
          return twoDecimalPoints(
            values?.GeschwindigkeitValue * (values?.stunden + minutesValue + values.sekunden / 3600)
          );
        } else if (values?.field0b === t("field1bOption1") && values?.field3bc === t("field0bOption2")) {
          return twoDecimalPoints(
            (values?.GeschwindigkeitValue * (values.stunden + values.minuten / 60 + values.sekunden / 3600)) / 3.6
          );
        } else if (values?.field0b === t("field1bOption1") && values?.field3bc === t("field0bOption3")) {
          return twoDecimalPoints(
            (values?.GeschwindigkeitValue * (values?.stunden + minutesValue + secondsValue)) / 1.60934
          );
        } else if (values?.field0b === t("field1bOption2") && values?.field3bc === t("field0bOption1")) {
          const result = 1000 * values?.GeschwindigkeitValue * (values?.stunden + minutesValue + secondsValue);
          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field1bOption2") && values?.field3bc === t("field0bOption2")) {
          const result = (1000 * values?.GeschwindigkeitValue * (values?.stunden + minutesValue + secondsValue)) / 3.6;

          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field1bOption2") && values?.field3bc === t("field0bOption3")) {
          const result =
            (1000 * values?.GeschwindigkeitValue * (values?.stunden + minutesValue + secondsValue)) / 1.60934;

          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field1bOption3") && values?.field3bc === t("field0bOption1")) {
          const result = (values?.GeschwindigkeitValue * (values?.stunden + minutesValue + secondsValue)) / 1.60934;
          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field1bOption3") && values?.field3bc === t("field0bOption2")) {
          const result =
            (values?.GeschwindigkeitValue * (values?.stunden + minutesValue + secondsValue)) / 3.6 / 1.60934;

          return twoDecimalPoints(result);
        } else if (values?.field0b === t("field1bOption3") && values?.field3bc === t("field0bOption3")) {
          const result = values?.GeschwindigkeitValue * (values?.stunden + minutesValue + secondsValue);
          return twoDecimalPoints(result);
        }
      }
    }
  };

  const getUnit = () => {
    let unit: string;
    if (values.field0a === t("field0aOption1")) {
      if (values.field0b === t("field0bOption1")) {
        unit = "km/h";
      } else if (values.field0b === t("field0bOption2")) {
        unit = "m/s";
      } else if (values.field0b === t("field0bOption3")) {
        unit = "mph";
      }
    } else if (values.field0a === t("field0aOption3")) {
      if (values.field0b === t("field1bOption1")) {
        unit = t("field1bOption1");
      } else if (values.field0b === t("field1bOption2")) {
        unit = t("field1bOption2");
      } else if (values.field0b === t("field1bOption3")) {
        unit = t("field1bOption3");
      }
    }

    return unit;
  };

  const handleCalculate = () => {
    const unit = getUnit();
    setValues((el) => ({
      ...el,
      speed: values?.field0a === t("field0aOption1") ? calculateSpeed() : initialState.speed,
      time: values?.field0a === t("field0aOption2") ? calculateTime() : initialState.time,
      distance: values?.field0a === t("field0aOption3") ? calculateDistance() : initialState.distance,
      hasError: false,
      displayUnit: unit ? unit : "",
    }));
  };

  return (
    <>
      <CalculatorImage
        src="/images/calculators/geschwindigkeit-rechner/Geschwindigkeitsrechner.png"
        width={isSmallDevice ? 450 : 750}
        height={isSmallDevice ? 150 : 350}
        layout="intrinsic"
        alt={t("altTextImage1")}
      />
      <CalculatorButton idToScrollTo="Rechner" scroll text={t("speedButton1")} />
      <CalculatorBodyText>{t("paragraph1")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph2")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph3")}</CalculatorBodyText>
      <div className={classes.listLinkContainer}>
        <div className={classes.listLinkHeader}>{t("listHeadline")}</div>
        <ListLinkText link={t("linkAnchor1")} linkName={t("list1")} />
        <ListLinkText link={t("linkAnchor2")} linkName={t("list2")} />
        <ListLinkText link={t("linkAnchor3")} linkName={t("list3")} />
        <ListLinkText link={t("linkAnchor4")} linkName={t("list4")} />
        <ListLinkText link={t("linkAnchor5")} linkName={t("list5")} />
        <ListLinkText link={t("linkAnchor6")} linkName={t("list6")} />
      </div>

      <div className={classes.headingTopMargin} id="Rechner">
        <CalculatorHeadline centered>{t("headline2")}</CalculatorHeadline>
      </div>

      <PreCalculatorHeadline tag="h2">{t("overline1")}</PreCalculatorHeadline>

      <div className={`${classes.alignCenter} ${classes.overline1}`}>
        <div style={{ display: "flex", marginTop: "37px" }}>
          <SelectorComponent value={values?.field0a} onChange={(e) => handleInputOnChange("field0a", e)}>
            {[t("field0aOption1"), t("field0aOption2"), t("field0aOption3")].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </SelectorComponent>
          {values.field0a === t("field0aOption2") ? (
            <div />
          ) : (
            <>
              <div className={classes.InBetween}>In</div>
              <div>
                <SelectorComponent value={values?.field0b} onChange={(e) => handleInputOnChange("field0b", e)}>
                  {values?.field0a === t("field0aOption1")
                    ? [t("field0bOption1"), t("field0bOption2"), t("field0bOption3")].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))
                    : [t("field1bOption1"), t("field1bOption2"), t("field1bOption3")].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                </SelectorComponent>
              </div>
            </>
          )}
        </div>
      </div>

      <InputHeader
        inputNumber="1"
        inputTitle={values?.field0a === t("field0aOption3") ? t("overline3") : t("overline2")}
      >
        {values?.field0a === t("field0aOption3") ? (
          <div className={classes.timeInput}>
            <InputWithMarginBottom
              label={t("stunden")}
              value={values?.stunden}
              onChange={(e) => handleInputOnChange("stunden", e)}
            />
            <InputWithMarginBottom
              label={t("minuten")}
              value={values?.minuten}
              onChange={(e) => handleInputOnChange("minuten", e)}
            />
            <InputWithMarginBottom
              label={t("sekunden")}
              value={values?.sekunden}
              onChange={(e) => handleInputOnChange("sekunden", e)}
            />
          </div>
        ) : (
          <div className={classes.flex}>
            <InputComponent
              onChange={(e) => handleInputOnChange("StreckeValue", e)}
              name={""}
              result=""
              value={values?.StreckeValue || ""}
            />

            <div className={classes.marginLeft}>
              <SelectorComponent value={values?.field1b} onChange={(e) => handleInputOnChange("field1b", e)}>
                {[t("field1bOption1"), t("field1bOption2"), t("field1bOption3")].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </SelectorComponent>
            </div>
          </div>
        )}
      </InputHeader>

      <InputHeader
        inputNumber="2"
        inputTitle={
          values.field0a === t("field0aOption2") || values.field0a === t("field0aOption3")
            ? t("overline3b")
            : t("overline3")
        }
      >
        {values.field0a === t("field0aOption2") || values.field0a === t("field0aOption3") ? (
          <div className={classes.flex}>
            <InputComponent
              onChange={(e) => handleInputOnChange("GeschwindigkeitValue", e)}
              name={""}
              result=""
              value={values?.GeschwindigkeitValue || ""}
            />

            <div className={classes.marginLeft}>
              <SelectorComponent value={values?.field3bc} onChange={(e) => handleInputOnChange("field3bc", e)}>
                {[t("field0bOption1"), t("field0bOption2"), t("field0bOption3")].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </SelectorComponent>
            </div>
          </div>
        ) : (
          <div className={classes.timeInput}>
            <InputWithMarginBottom
              label={t("stunden")}
              value={values?.stunden}
              onChange={(e) => handleInputOnChange("stunden", e)}
            />
            <InputWithMarginBottom
              label={t("minuten")}
              value={values?.minuten}
              onChange={(e) => handleInputOnChange("minuten", e)}
            />
            <InputWithMarginBottom
              label={t("sekunden")}
              value={values?.sekunden}
              onChange={(e) => handleInputOnChange("sekunden", e)}
            />
          </div>
        )}
      </InputHeader>

      <CalculatorButton
        onClick={() => {
          handleCalculate();
        }}
        text={t("calculatorBtn")}
      />

      {values?.hasError ? <p className={classes.errorMessage}>{t("errorMessage")}</p> : null}

      {values?.speed !== null ||
      values?.distance !== null ||
      values?.time?.Z1 !== null ||
      values?.time?.Z2 !== null ||
      values?.time?.Z3 !== null ? (
        <div className={`${classes.alignCenter}`}>
          <CalculatorResultsTitle text={t("resultHeadline")} />
          <CalculatorTopicH2Headline>
            {values?.field0a === t("field0aOption1")
              ? t("result1SubHeadline")
              : values?.field0a === t("field0aOption2")
              ? t("result2SubHeadline")
              : t("result3SubHeadline")}
          </CalculatorTopicH2Headline>

          {values?.field0a === t("field0aOption1") && values?.speed !== null && (
            <ResultWithColors
              results={[
                {
                  value: values.speed,
                  color: "#04B0E9",
                  valueB: `${!values.displayUnit ? "km/h" : values.displayUnit}`,
                },
              ]}
            />
          )}

          {values?.field0a === t("field0aOption2") && (values?.time.Z1 || values?.time.Z2 || values?.time.Z3) && (
            <ResultWithColors
              results={[
                {
                  value: `${!values.time.Z1 ? "" : `${values?.time.Z1} Stunden`} ${
                    !values.time.Z2 ? "" : `${values?.time.Z2} Minuten`
                  } `,
                  color: "#04B0E9",
                  valueB: `${(!values.time.Z1 && !values.time.Z2) || !values.time.Z3 ? "" : "und"}`,
                  valueC: values.time.Z3 ? `${values.time.Z3} ${t("sekunden")}` : "",
                },
              ]}
            />
          )}

          {values?.field0a === t("field0aOption3") && values?.distance !== null && (
            <ResultWithColors
              results={[
                {
                  value: values.distance,
                  color: "#04B0E9",
                  valueB: `${!values.displayUnit ? "Kilometer" : values.displayUnit}`,
                },
              ]}
            />
          )}
        </div>
      ) : null}
      <CycleverseDivider marginTop={8} marginBottom={8} />
      <div id="Geschwindigkeit-Entfernung-Zeit">
        <CalculatorTopicH2Headline>{t("paraHeadline1")}</CalculatorTopicH2Headline>
      </div>
      <CalculatorBodyText>{t("paragraph4")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph5")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph7")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph8")}</CalculatorBodyText>
      <div id="Formeln">
        <CalculatorTopicH2Headline>{t("paraHeadline2")}</CalculatorTopicH2Headline>
      </div>
      <CalculatorBodyText>{t("paragraph9")}</CalculatorBodyText>
      <CalculatorTopicHeadline color="#009fe5">{t("paraHeadline3")}</CalculatorTopicHeadline>
      <CalculatorBodyText color="#fd4901">
        <strong>{t("paragraph10Title")}</strong> <br />
        {t("paragraph10a")} <br />
        {t("paragraph10b")}
      </CalculatorBodyText>

      <CalculatorBodyText color="#fd4901">
        <strong>{t("paragraph11Title")}</strong> <br />
        {t("paragraph11a")} <br />
        {t("paragraph11b")}
      </CalculatorBodyText>
      <CalculatorTopicHeadline>{t("paraHeadline4")}</CalculatorTopicHeadline>
      <CalculatorBodyText>{t("paragraph12")}</CalculatorBodyText>
      <CalculatorTopicHeadline color="#009fe5">{t("paraHeadline5")}</CalculatorTopicHeadline>
      <CalculatorBodyText>{t("paragraph13")}</CalculatorBodyText>
      <CalculatorBodyText color="#fd4901">
        <strong>{t("paragraph14Title")}</strong> <br />
        {t("paragraph14a")} <br />
        {t("paragraph14b")} <br />
        {t("paragraph14c")}
      </CalculatorBodyText>
      <CalculatorTopicHeadline>{t("paraHeadline6")}</CalculatorTopicHeadline>
      <CalculatorImage
        src="/images/calculators/geschwindigkeit-rechner/Geschwindigkeitsrechner-Skateboard.jpeg"
        width={800}
        layout="intrinsic"
        alt={t("image1Alt")}
      />
      <CalculatorBodyText className={classes.imgDescription}>{t("image1Desc")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph15")}</CalculatorBodyText>
      <CalculatorTopicHeadline color="#009fe5">{t("paraHeadline7")}</CalculatorTopicHeadline>
      <CalculatorBodyText color="#fd4901">
        <strong>{t("paragraph16Title")}</strong> <br />
        {t("paragraph16a")} <br />
        {t("paragraph16b")}
      </CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph17")}</CalculatorBodyText>
      <div id="Umrechnung">
        <CalculatorTopicH2Headline>{t("paraHeadline8")}</CalculatorTopicH2Headline>
      </div>
      <CalculatorBodyText>{t("paragraph18")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph19")}</CalculatorBodyText>
      <CalculatorTopicHeadline color="#009fe5">{t("paraHeadline9")}</CalculatorTopicHeadline>
      <CalculatorBodyText>{t("paragraph20")}</CalculatorBodyText>
      <CalculatorTopicHeadline color="#009fe5">{t("paraHeadline10")}</CalculatorTopicHeadline>
      <CalculatorBodyText color="#fd4901">
        <strong>{t("paragraph21Title")}</strong> <br />
        {t("paragraph21a")} <br />
        {t("paragraph21b")}
      </CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph22")}</CalculatorBodyText>
      <CalculatorTopicHeadline color="#009fe5">{t("paraHeadline11")}</CalculatorTopicHeadline>
      <CalculatorBodyText color="#fd4901">
        <strong>{t("paragraph23Title")}</strong> <br />
        {t("paragraph23a")} <br />
        {t("paragraph23b")} <br />
        {t("paragraph23c")} <br />
        {t("paragraph23d")} <br />
        {t("paragraph23e")} <br />
      </CalculatorBodyText>
      <div id="Tipps-Geschwindigkeitsoptimierung">
        <CalculatorTopicHeadline>{t("paraHeadline12")}</CalculatorTopicHeadline>
      </div>
      <CalculatorImage
        src="/images/calculators/geschwindigkeit-rechner/Geschwindigkeitsrechner-Rennrad.jpeg"
        width={800}
        layout="intrinsic"
        alt={t("image2Alt")}
      />
      <CalculatorBodyText className={classes.imgDescription}>{t("image2Desc")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph24")}</CalculatorBodyText>
      <CalculatorBodyText>
        <strong>{t("paragraph25BoldText")} </strong>
        {t("paragraph25")}
      </CalculatorBodyText>
      <CalculatorTopicHeadline>{t("paraHeadline13")}</CalculatorTopicHeadline>
      <CalculatorBodyText>{t("paragraph26")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph27")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph28")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph29")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph30")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph31")}</CalculatorBodyText>
      <div id="Geschwindigkeit-Arten">
        <CalculatorTopicH2Headline>{t("paraHeadline14")}</CalculatorTopicH2Headline>
      </div>
      <CalculatorBodyText>{t("paragraph32")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph34")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph35")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph36")}</CalculatorBodyText>
      <CalculatorBodyText>{t("paragraph38")}</CalculatorBodyText>
      <CalculatorButton idToScrollTo="Rechner" scroll text={t("speedButton1")} />
    </>
  );
};

export default GeschwindigkeitRechner;
